import { useMemo, useState } from 'react';
import { ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export type FilterMultiSelectOption = {
  value: string;
  label: string;
};

export interface FilterMultiSelectProps {
  values: string[];
  onValuesChange: (values: string[]) => void;
  options: FilterMultiSelectOption[];
  placeholder?: string;
  allLabel?: string;
  className?: string;
  triggerClassName?: string;
  /** Popover panel width (defaults to at least trigger width, expands for long labels). */
  contentClassName?: string;
  disabled?: boolean;
}

export function FilterMultiSelect({
  values,
  onValuesChange,
  options,
  placeholder = 'Select…',
  allLabel = 'All',
  className,
  triggerClassName,
  contentClassName,
  disabled = false,
}: FilterMultiSelectProps) {
  const [open, setOpen] = useState(false);

  const labelByValue = useMemo(
    () => new Map(options.map((o) => [o.value, o.label])),
    [options]
  );

  const triggerLabel = useMemo(() => {
    if (!values.length) return allLabel || placeholder;
    if (values.length === 1) return labelByValue.get(values[0]) ?? values[0];
    return `${values.length} selected`;
  }, [allLabel, labelByValue, placeholder, values]);

  const toggle = (value: string) => {
    onValuesChange(
      values.includes(value) ? values.filter((v) => v !== value) : [...values, value]
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'h-9 justify-between border-gray-200 bg-white px-3 text-sm font-normal text-gray-900 hover:bg-white',
            !values.length && 'text-gray-500',
            triggerClassName,
            className
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          'min-w-[var(--radix-popover-trigger-width)] w-max max-w-[min(90vw,36rem)] p-2',
          contentClassName
        )}
        align="start"
      >
        <div className="max-h-60 space-y-1 overflow-y-auto">
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-50"
            onClick={() => onValuesChange([])}
          >
            <Checkbox checked={!values.length} onCheckedChange={() => onValuesChange([])} />
            <span className="whitespace-normal break-words">{allLabel}</span>
          </button>
          {options.map((option) => {
            const checked = values.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                className="flex w-full items-start gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-50"
                onClick={() => toggle(option.value)}
              >
                <Checkbox
                  className="mt-0.5"
                  checked={checked}
                  onCheckedChange={() => toggle(option.value)}
                />
                <span className="min-w-0 flex-1 whitespace-normal break-words">{option.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
